/**
 * NeuNexus Energy — Uetze Consortium Executive Pitch.
 * 15 native code-rendered slides (no PDF images), fully localised PT/EN/DE.
 * Language auto-detected via react-i18next (shared with the rest of HannoNexus).
 */
import React from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, ArrowRight, Home as HomeIcon, Maximize2, X, Play, Pause, Globe, ChevronDown, Check } from "lucide-react";
import { Flag } from "@/components/hx/Flag";
import { SLIDES, UI, SUPPORTED } from "./slidesData";
import { SlideRenderer } from "./SlideRenderer";

/**
 * Local language switcher for /neunexus — swaps i18n language IN-PLACE
 * without navigating (the global switcher rewrites URLs to /pt/... which
 * the global router doesn't support for this route).
 */
const LANG_META = {
  pt: { name: "Português", short: "PT" },
  en: { name: "English",   short: "EN" },
  de: { name: "Deutsch",   short: "DE" },
};

function InlineLangSwitcher({ current, onChange }) {
  const [open, setOpen] = React.useState(false);
  const ref = React.useRef(null);
  React.useEffect(() => {
    const onClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);
  const meta = LANG_META[current] || LANG_META.en;
  return (
    <div ref={ref} className="relative" data-testid="neunexus-lang-switcher">
      <button onClick={() => setOpen((v) => !v)}
        data-testid="neunexus-lang-button"
        className="flex items-center gap-2 px-3 py-2 rounded-md transition-all border"
        style={{ borderColor: open ? "rgba(20,255,140,0.4)" : "rgba(20,255,140,0.18)", background: "rgba(20,255,140,0.04)" }}>
        <Globe size={14} style={{ color: "#14FF8C" }} />
        <Flag code={current} />
        <span className="font-mono text-[11px] uppercase tracking-[0.18em] text-white">{meta.short}</span>
        <ChevronDown size={12} className={`transition-transform ${open ? "rotate-180" : ""}`} style={{ color: "#7DD3A9" }} />
      </button>
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -8, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -8, scale: 0.96 }}
            transition={{ duration: 0.18, ease: "easeOut" }}
            className="absolute top-full right-0 mt-2 w-52 rounded-lg overflow-hidden z-50 shadow-2xl"
            style={{ background: "rgba(4,7,14,0.98)", border: "1px solid rgba(20,255,140,0.25)", backdropFilter: "blur(20px)" }}>
            <div className="p-2">
              {SUPPORTED.map((code) => {
                const m = LANG_META[code];
                const active = code === current;
                return (
                  <button key={code} onClick={() => { onChange(code); setOpen(false); }}
                    data-testid={`neunexus-lang-option-${code}`}
                    className="w-full flex items-center gap-3 px-3 py-2.5 rounded-md transition-all"
                    style={{ background: active ? "rgba(20,255,140,0.10)" : "transparent", color: active ? "#14FF8C" : "#E4F1FF" }}>
                    <Flag code={code} className="text-xl" />
                    <div className="flex-1 text-left">
                      <div className="font-semibold text-[13px]">{m.name}</div>
                      <div className="text-[10px] uppercase tracking-[0.22em]" style={{ color: "#7DD3A9" }}>{m.short}</div>
                    </div>
                    {active && <Check size={14} style={{ color: "#14FF8C" }} />}
                  </button>
                );
              })}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default function NeuNexusPage() {
  const { i18n } = useTranslation();
  const raw = (i18n.language || "en").split("-")[0];
  const lang = SUPPORTED.includes(raw) ? raw : "en";
  const u = UI[lang];

  const [idx, setIdx] = React.useState(0);
  const [playing, setPlaying] = React.useState(false);
  const [zoom, setZoom] = React.useState(false);

  const next = React.useCallback(() => setIdx((i) => (i + 1) % SLIDES.length), []);
  const prev = React.useCallback(() => setIdx((i) => (i - 1 + SLIDES.length) % SLIDES.length), []);
  const [blueprintOpen, setBlueprintOpen] = React.useState(false);

  React.useEffect(() => {
    const onKey = (e) => {
      if (e.key === "ArrowRight" || e.key === " ") { e.preventDefault(); next(); }
      else if (e.key === "ArrowLeft") { e.preventDefault(); prev(); }
      else if (e.key === "Escape") setZoom(false);
      else if (e.key === "f" || e.key === "F") setZoom((z) => !z);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [next, prev]);

  React.useEffect(() => {
    if (!playing) return;
    const t = setTimeout(next, 8500);
    return () => clearTimeout(t);
  }, [playing, idx, next]);

  const slide = SLIDES[idx];
  const title = slide.i18n[lang]?.title || slide.i18n.en.title;

  return (
    <div className="min-h-screen relative overflow-hidden"
      style={{ background: "#04070E", color: "#E4F1FF", fontFamily: "'Inter', 'Poppins', system-ui, sans-serif" }}
      data-testid="neunexus-page">

      {/* Ambient grid + glow */}
      <div className="fixed inset-0 pointer-events-none" style={{
        backgroundImage: "linear-gradient(rgba(20,255,140,0.04) 1px, transparent 1px), linear-gradient(90deg, rgba(20,255,140,0.04) 1px, transparent 1px)",
        backgroundSize: "48px 48px",
      }} />
      <div className="fixed -top-40 -right-40 w-[600px] h-[600px] rounded-full pointer-events-none"
        style={{ background: "radial-gradient(circle, rgba(20,255,140,0.12) 0%, transparent 65%)" }} />

      {/* Header */}
      <header className="relative z-20 border-b" style={{ borderColor: "rgba(20,255,140,0.15)", background: "rgba(4,7,14,0.85)", backdropFilter: "blur(12px)" }}>
        <div className="max-w-7xl mx-auto flex items-center justify-between gap-6 px-6 py-4">
          <Link to="/" className="flex items-center gap-3 group" data-testid="neunexus-home-link">
            <div className="w-10 h-10 rounded-md flex items-center justify-center"
              style={{ background: "linear-gradient(135deg, #14FF8C 0%, #00A86B 100%)", boxShadow: "0 0 24px -4px #14FF8C88" }}>
              <span className="font-bold text-[15px] text-[#04070E]">N</span>
            </div>
            <div className="leading-none">
              <div className="font-bold text-[15px] tracking-tight">NeuNexus Energy</div>
              <div className="text-[9px] uppercase tracking-[0.28em] mt-1" style={{ color: "#7DD3A9" }}>{u.consortium}</div>
            </div>
          </Link>

          <div className="hidden md:flex items-center gap-2 text-[10.5px] uppercase tracking-[0.22em]" style={{ color: "#7DD3A9" }}>
            <span>Siemens</span><span className="opacity-40">·</span>
            <span>VCOW</span><span className="opacity-40">·</span>
            <span>DFOPTIX</span>
          </div>

          <div className="flex items-center gap-3">
            <InlineLangSwitcher current={lang} onChange={(code) => i18n.changeLanguage(code)} />
            <Link to="/" className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] hover:text-white transition"
              style={{ color: "#7DD3A9" }} data-testid="neunexus-back-link">
              <HomeIcon size={13} /> {u.back}
            </Link>
          </div>
        </div>
      </header>

      {/* Main area */}
      <main className="relative z-10 max-w-[1400px] mx-auto px-4 lg:px-8 pt-8 pb-10">
        {/* Slide meta */}
        <div className="flex items-center justify-between mb-5 gap-4">
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-[0.32em] font-semibold" style={{ color: "#14FF8C" }}>
              {u.slide} {String(slide.n).padStart(2, "0")} / {SLIDES.length}
              <span className="ml-3 opacity-60">· {u.intro}</span>
            </div>
            <div className="font-semibold text-[15px] sm:text-[17px] mt-2 tracking-tight text-white truncate">
              {title}
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <button onClick={() => setPlaying((p) => !p)}
              data-testid="neunexus-play-toggle"
              aria-label={playing ? "Pause" : "Play"}
              className="w-11 h-11 rounded-full flex items-center justify-center transition hover:scale-105"
              style={{ background: playing ? "rgba(20,255,140,0.18)" : "rgba(20,255,140,0.08)", border: "1px solid rgba(20,255,140,0.4)", color: "#14FF8C" }}>
              {playing ? <Pause size={15} /> : <Play size={15} />}
            </button>
            <button onClick={() => setZoom(true)}
              data-testid="neunexus-zoom"
              aria-label="Fullscreen"
              className="w-11 h-11 rounded-full flex items-center justify-center transition hover:scale-105"
              style={{ background: "rgba(20,255,140,0.08)", border: "1px solid rgba(20,255,140,0.4)", color: "#14FF8C" }}>
              <Maximize2 size={15} />
            </button>
          </div>
        </div>

        {/* Slide viewport */}
        <motion.div
          key={`${idx}-${lang}`}
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, ease: "easeOut" }}
          className="relative rounded-lg overflow-hidden"
          data-testid="neunexus-slide-viewport"
          style={{
            border: "1px solid rgba(20,255,140,0.22)",
            boxShadow: "0 40px 100px -30px rgba(20,255,140,0.18), 0 2px 0 rgba(20,255,140,0.06) inset",
            background: "#04070E",
          }}>
          <SlideRenderer slide={slide} lang={lang} />
        </motion.div>

        {/* Blueprint reference — original PDF image */}
        <motion.section
          key={`bp-${idx}`}
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, ease: "easeOut", delay: 0.15 }}
          className="mt-8 rounded-lg overflow-hidden"
          data-testid="neunexus-blueprint-ref"
          style={{ border: "1px solid rgba(20,255,140,0.18)", background: "rgba(20,255,140,0.03)" }}>
          <div className="flex items-center justify-between gap-4 px-5 py-3 border-b" style={{ borderColor: "rgba(20,255,140,0.15)" }}>
            <div>
              <div className="text-[9px] uppercase tracking-[0.32em] font-semibold" style={{ color: "#14FF8C" }}>
                {u.blueprintRef}
              </div>
              <div className="text-[11px] mt-1" style={{ color: "#7DD3A9" }}>{u.blueprintHint}</div>
            </div>
            <button onClick={() => setBlueprintOpen(true)}
              data-testid="neunexus-blueprint-expand"
              className="w-10 h-10 rounded-full flex items-center justify-center transition hover:scale-105 shrink-0"
              style={{ background: "rgba(20,255,140,0.10)", border: "1px solid rgba(20,255,140,0.4)", color: "#14FF8C" }}
              aria-label="Expand blueprint">
              <Maximize2 size={14} />
            </button>
          </div>
          <button onClick={() => setBlueprintOpen(true)} className="block w-full cursor-zoom-in" data-testid="neunexus-blueprint-img">
            <img
              src={`/neunexus/slide-${String(slide.n).padStart(2, "0")}.jpg`}
              alt={`Blueprint page ${slide.n}`}
              loading="lazy"
              className="w-full h-auto block transition hover:opacity-95"
            />
          </button>
        </motion.section>

        {/* Controls */}
        <div className="mt-6 flex items-center justify-between gap-4">
          <button onClick={prev} data-testid="neunexus-prev"
            className="inline-flex items-center gap-2 px-5 py-3 rounded-full text-[11px] uppercase tracking-[0.22em] font-semibold transition hover:translate-x-[-2px]"
            style={{ border: "1px solid rgba(20,255,140,0.35)", background: "rgba(20,255,140,0.06)", color: "#14FF8C" }}>
            <ArrowLeft size={13} /> {u.prev}
          </button>

          <div className="flex items-center gap-1.5 overflow-x-auto max-w-[60%] scrollbar-hide">
            {SLIDES.map((s, i) => (
              <button key={s.id} onClick={() => setIdx(i)}
                data-testid={`neunexus-dot-${s.n}`}
                aria-label={`Go to slide ${s.n}`}
                className="shrink-0 transition-all"
                style={{
                  width: i === idx ? 28 : 8,
                  height: 8,
                  borderRadius: 4,
                  background: i === idx ? "#14FF8C" : "rgba(20,255,140,0.22)",
                  boxShadow: i === idx ? "0 0 12px -2px #14FF8C" : "none",
                }} />
            ))}
          </div>

          <button onClick={next} data-testid="neunexus-next"
            className="inline-flex items-center gap-2 px-5 py-3 rounded-full text-[11px] uppercase tracking-[0.22em] font-bold transition hover:translate-x-[2px]"
            style={{ background: "#14FF8C", color: "#04070E", boxShadow: "0 12px 32px -8px rgba(20,255,140,0.6)" }}>
            {u.next} <ArrowRight size={13} />
          </button>
        </div>
      </main>

      {/* Fullscreen mode */}
      {zoom && (
        <div className="fixed inset-0 z-[9999] p-4 sm:p-10 overflow-auto"
          style={{ background: "rgba(4,7,14,0.98)", backdropFilter: "blur(20px)" }}
          data-testid="neunexus-lightbox">
          <button onClick={() => setZoom(false)}
            className="fixed top-5 right-5 w-11 h-11 rounded-full flex items-center justify-center transition hover:scale-110 z-[10000]"
            style={{ background: "rgba(20,255,140,0.15)", border: "1px solid rgba(20,255,140,0.5)", color: "#14FF8C" }}
            aria-label="Close"
            data-testid="neunexus-lightbox-close">
            <X size={18} />
          </button>
          <div className="max-w-[1500px] mx-auto rounded-lg overflow-hidden"
            style={{ border: "1px solid rgba(20,255,140,0.22)", background: "#04070E" }}>
            <SlideRenderer slide={slide} lang={lang} />
          </div>
          <div className="max-w-[1500px] mx-auto mt-6 flex items-center justify-between gap-4">
            <button onClick={prev} className="inline-flex items-center gap-2 px-5 py-3 rounded-full text-[11px] uppercase tracking-[0.22em] font-semibold"
              style={{ border: "1px solid rgba(20,255,140,0.35)", background: "rgba(20,255,140,0.06)", color: "#14FF8C" }}>
              <ArrowLeft size={13} /> {u.prev}
            </button>
            <span className="text-[10.5px] uppercase tracking-[0.28em]" style={{ color: "#7DD3A9" }}>
              {u.slide} {String(slide.n).padStart(2, "0")} / {SLIDES.length}
            </span>
            <button onClick={next} className="inline-flex items-center gap-2 px-5 py-3 rounded-full text-[11px] uppercase tracking-[0.22em] font-bold"
              style={{ background: "#14FF8C", color: "#04070E" }}>
              {u.next} <ArrowRight size={13} />
            </button>
          </div>
        </div>
      )}

      {/* Blueprint image lightbox */}
      {blueprintOpen && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 sm:p-8"
          style={{ background: "rgba(4,7,14,0.98)", backdropFilter: "blur(20px)" }}
          onClick={() => setBlueprintOpen(false)}
          data-testid="neunexus-blueprint-lightbox">
          <button onClick={(e) => { e.stopPropagation(); setBlueprintOpen(false); }}
            className="absolute top-5 right-5 w-11 h-11 rounded-full flex items-center justify-center transition hover:scale-110"
            style={{ background: "rgba(20,255,140,0.15)", border: "1px solid rgba(20,255,140,0.5)", color: "#14FF8C" }}
            aria-label={u.blueprintClose}
            data-testid="neunexus-blueprint-lightbox-close">
            <X size={18} />
          </button>
          <img src={`/neunexus/slide-${String(slide.n).padStart(2, "0")}.jpg`}
            alt={`Blueprint page ${slide.n}`}
            className="max-w-full max-h-[92vh] object-contain rounded-md"
            onClick={(e) => e.stopPropagation()}
            style={{ boxShadow: "0 40px 100px -20px rgba(20,255,140,0.3)" }} />
        </div>
      )}

      {/* Footer */}
      <footer className="relative z-10 border-t py-6 px-6" style={{ borderColor: "rgba(20,255,140,0.15)" }}>
        <div className="max-w-7xl mx-auto flex items-center justify-between flex-wrap gap-4 text-[10px] uppercase tracking-[0.24em]" style={{ color: "#7DD3A9" }}>
          <div>© NeuNexus Energy · Uetze · Niedersachsen · Germany</div>
          <div>{u.printedFrom}</div>
          <Link to="/" className="hover:text-white transition">← HannoNexus</Link>
        </div>
      </footer>
    </div>
  );
}
